// Server-side recommendations.
//
// Replaces the client-side scorer in engine.ts for live catalogs. engine.ts
// remains the fixture-mode implementation and the executable spec the server
// was built against — same signal keys, same preset weights, same
// normalize-by-weight-sum blend — which is why a card renders identically
// whichever produced it.
//
// The move matters because the client only ever holds a page: scoring locally
// could rank a few hundred works, while the server scores every candidate that
// shares a tag with the seed, out of tens of thousands.

import { Code, ConnectError } from "@connectrpc/connect";

import { recommendClient } from "../lib/connect";
import type { Filters, Kind, Recommendation, Signal, SignalKey } from "./types";
import { PRESETS } from "./engine";

export interface RecommendOutcome {
  recommendations: Recommendation[];
  /** Set when the server can't recommend, so the UI can say why. */
  error?: string;
}

// Proto SignalKind enum names → the UI's short keys. Signals the client has no
// label for are dropped rather than rendered as "UNKNOWN".
const SIGNAL_KEYS: Record<number, SignalKey> = {
  1: "tags",   // TAG_SIMILARITY
  7: "pop",    // POPULARITY
  8: "mood",   // MOOD_SIMILARITY
  9: "score",  // SCORE
  10: "obs",   // OBSCURITY
  11: "len",   // LENGTH_FIT
  6: "by",     // SHARED_CREDITS
};

const SIGNAL_LABELS: Record<SignalKey, string> = {
  tags: "Genre similarity", mood: "Mood similarity", score: "Site score",
  pop: "Popularity", obs: "Obscurity", by: "Creator match", len: "Length fit",
};

export async function recommendSimilar(
  workId: string, presetKey: string, kind: Kind, filters: Filters, limit: number, signal?: AbortSignal,
): Promise<RecommendOutcome> {
  // Genre/mood/length/status chips stay CLIENT-side for now: they narrow an
  // already-scored set, and pushing them into the candidate query would
  // shrink the pool before scoring — turning "top 60 of everything similar,
  // then filtered" into "top 60 of the filtered slice", which surfaces much
  // weaker matches. Year bounds are cheap and safe to push down.
  void filters;
  const preset = PRESETS.find((p) => p.key === presetKey) ?? PRESETS[0];
  try {
    const res = await recommendClient.recommendSimilar({
      workId,
      limit,
      // Explanations are what make a bad recommendation debuggable rather than
      // mysterious, and they're the card's one-line "why".
      explain: true,
      // Sequels and adaptations of the seed are its nearest neighbours and the
      // least useful thing to suggest.
      excludeRelated: true,
      filters: {
        kinds: [kind === "anime" ? 1 : 2],
        yearFrom: filters.yearFrom ?? 0,
        yearTo: filters.yearTo ?? 0,
      },
      weights: { weights: toProtoWeights(preset.key) },
    }, { signal });

    return { recommendations: res.recommendations.map(toRecommendation) };
  } catch (err) {
    if (signal?.aborted) throw err;
    if (err instanceof ConnectError) {
      if (err.code === Code.Unimplemented) {
        return { recommendations: [], error: "the backend has no recommender yet" };
      }
      // FAILED_PRECONDITION is the honest "this seed has no tags" answer —
      // surfaced verbatim rather than silently falling back to popularity.
      return { recommendations: [], error: err.rawMessage };
    }
    return { recommendations: [], error: "recommendations unavailable" };
  }
}

/** Preset weights keyed by the proto enum names the server expects. */
function toProtoWeights(presetKey: string): Record<string, number> {
  const p = PRESETS.find((x) => x.key === presetKey) ?? PRESETS[0];
  const map: Record<SignalKey, string> = {
    tags: "SIGNAL_KIND_TAG_SIMILARITY", mood: "SIGNAL_KIND_MOOD_SIMILARITY",
    score: "SIGNAL_KIND_SCORE", pop: "SIGNAL_KIND_POPULARITY",
    obs: "SIGNAL_KIND_OBSCURITY", by: "SIGNAL_KIND_SHARED_CREDITS",
    len: "SIGNAL_KIND_LENGTH_FIT",
  };
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(p.weights)) {
    if (v != null) out[map[k as SignalKey]] = v;
  }
  return out;
}

type ProtoRec = Awaited<ReturnType<typeof recommendClient.recommendSimilar>>["recommendations"][number];

function toRecommendation(r: ProtoRec): Recommendation {
  const w = r.work!;
  const kind: Kind = w.kind === 1 ? "anime" : "manga";
  const units = kind === "anime" ? w.episodeCount : w.chapterCount;
  const score = w.stats?.[0]?.score ?? 0;

  const signals: Signal[] = (r.signals ?? [])
    .map((s): Signal | null => {
      const key = SIGNAL_KEYS[s.kind];
      if (!key) return null;
      return {
        key,
        label: SIGNAL_LABELS[key],
        value: s.rawScore,
        note: s.explanation,
        contribution: s.weighted,
      };
    })
    .filter((s): s is Signal => s !== null);

  return {
    id: w.id,
    kind,
    title: w.displayTitle,
    year: w.startDate?.year ?? 0,
    by: w.organizations?.find((o) => o.isMain)?.name ?? w.organizations?.[0]?.name ?? "Unknown",
    genres: (w.tags ?? []).filter((t) => t.tag?.category === "genre").map((t) => t.tag!.name),
    moods: (w.tags ?? []).filter((t) => t.tag?.category === "mood").map((t) => t.tag!.name),
    score: score / 10,
    pop: 0,
    len: units <= 13 ? "short" : units <= 50 ? "medium" : "long",
    status: w.status === 2 ? "airing" : w.status === 5 ? "hiatus" : "finished",
    count: units ? `${units} ${kind === "anime" ? "eps" : "ch"}` : "—",
    coverPath: w.cover?.proxyPath || undefined,
    match: Math.round(r.score),
    signals,
    // Top two signals that actually fired, in the server's own words.
    why: signals.filter((s) => s.value > 0.15 && s.note).slice(0, 2).map((s) => s.note).join(" · "),
  };
}
